import LegacyExperience from './GeneralInfo.legacy';
import { versioned } from '../../versioning/RenewalExperience';
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { Row, Col, Card, Form } from 'react-bootstrap';
import PayScheduleFrequency from './PayScheduleFrequency';
import axios from 'axios';
import debounce from 'lodash/debounce';
import Select from 'react-select';
import config from '../../config';

// Constants for hardcoded strings
const INFORMATION_PROVIDERS = {
  NOT_INDICATED: "Not Indicated",
  PREFUNDED_HRA: "Does not apply for Prefunded HRA Plans",
  VENDOR_FILE: "Vendor File",
};

const GeneralInfo = forwardRef(({ caseId, renewalCase, isDisableForUsers, onError = () => {}, setInformationProvider }, ref) => {
  const [generalInfo, setGeneralInfo] = useState({
    informationProvider: "",
    isInformationProviderChanged: false,
    updatedInformationProvider: "",
    payrollProviderVendorName: "",
    payrollProviderOtherVendorName: "",
    payrollProviderContactEmail: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const payscheduleRef = useRef();

  const InformationProviderOptions = [
    { value: "Autopost", label: "Clarity Autopost according to the provided payroll calendar(s). CANNOT BE USED FOR HSA" },
    { value: "Contribution Manager", label: "Contribution Manager through the Clarity Employer Portal" },
    { value: "Client File", label: "Deposit File uploaded to Clarity File Manager Tool" },
    { value: "Clarity Comprehensive API", label: "Integration with Clarity’s Comprehensive API" },
    { value: "ADP Marketplace", label: "ADP Marketplace connection" },
  ];

  const onlyHra = renewalCase?.products === 'HRA';
  const containsFSA = renewalCase?.products?.includes('FSA');

  // Fetch general information
  useEffect(() => {
    const fetchGeneralInfo = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/EBGeneralInformations/ByCaseId/${caseId}`);
        setGeneralInfo(response.data || {});
        setInformationProvider(
          response.data?.isInformationProviderChanged
            ? response.data?.updatedInformationProvider
            : response.data?.informationProvider
        );
      } catch (error) {
        console.error('Error fetching general information:', error);
        setError('Failed to fetch general information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchGeneralInfo();
  }, [caseId, setInformationProvider]);

  // Debounced update functions
  const debouncedUpdate = useCallback(
    debounce(async (updatedFields) => {
      try {
        const patchDocument = Object.keys(updatedFields).map((key) => ({
          path: `/${key}`,
          value: updatedFields[key],
        }));
        await axios.patch(`${config.API_URL}/EBGeneralInformations/${generalInfo?.id}`, patchDocument);
      } catch (error) {
        console.error('Error updating plan:', error);
        onError(error);
      }
    }, 500),
    [onError, generalInfo?.id]
  );

  const debouncedUpdateOnlyHRA = useCallback(
    debounce(async (id) => {
        
        const updatedFields= {'informationProvider': INFORMATION_PROVIDERS.PREFUNDED_HRA }
       
        try {
            const patchDocument = Object.keys(updatedFields).map(key => ({
                path: `/${key}`,
                value: updatedFields[key]
            }));
            await axios.patch(`${config.API_URL}/EBGeneralInformations/${id}`, patchDocument);
            setGeneralInfo(prevInfo => {
                const updatedInfo = { ...prevInfo, 'informationProvider': INFORMATION_PROVIDERS.PREFUNDED_HRA };
                return updatedInfo;
            });
        } catch (error) {
            console.error('Error updating plan:', error);
            onError(error);
        }

    }, 500),
    [onError, validationErrors, caseId]
);
const debouncedUpdateOnlyFSA = useCallback(
    debounce(async (id) => {
        
        const updatedFields= {'informationProvider': INFORMATION_PROVIDERS.NOT_INDICATED, 'isInformationProviderChanged': true }
       
        try {
            const patchDocument = Object.keys(updatedFields).map(key => ({
                path: `/${key}`,
                value: updatedFields[key]
            }));
            await axios.patch(`${config.API_URL}/EBGeneralInformations/${id}`, patchDocument);
            setGeneralInfo(prevInfo => {
                const updatedInfo = { ...prevInfo, 'informationProvider': INFORMATION_PROVIDERS.NOT_INDICATED };
                return updatedInfo;
            });
        } catch (error) {
            console.error('Error updating plan:', error);
            onError(error);
        }

    }, 500),
    [onError, validationErrors, caseId]
);



useEffect(() => {
    if (
      onlyHra &&
      !['Autopost', 'Contribution Manager', 'Client File', 'Vendor File', INFORMATION_PROVIDERS.NOT_INDICATED, INFORMATION_PROVIDERS.PREFUNDED_HRA].includes(
        generalInfo?.informationProvider?.trim()
      )
    ) {
      debouncedUpdateOnlyHRA(generalInfo?.id);
    }
  
    if (
      containsFSA &&
      !['Autopost', 'Contribution Manager', 'Client File', 'Vendor File', INFORMATION_PROVIDERS.NOT_INDICATED, INFORMATION_PROVIDERS.PREFUNDED_HRA].includes(
        generalInfo?.informationProvider?.trim()
      )
    ) {
      debouncedUpdateOnlyFSA(generalInfo?.id);
    }
  }, [onlyHra, containsFSA, generalInfo?.informationProvider, generalInfo?.id]);


  const handleToggleChange = (e, property) => {
    const { checked } = e.target;
    setGeneralInfo((prevInfo) => {
      const updatedInfo = { ...prevInfo, [property]: checked };
      debouncedUpdate({ [property]: checked });
      return updatedInfo;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
    setGeneralInfo((prevInfo) => {
      const updatedInfo = { ...prevInfo, [name]: value };
      debouncedUpdate({ [name]: value });
      return updatedInfo;
    });
  };

  const handleSelectChange = (selectedOption, { name }) => {
    const value = selectedOption ? selectedOption.value : '';
    validateField(name, value);
    setGeneralInfo((prevInfo) => {
      const updatedInfo = { ...prevInfo, [name]: value };
      debouncedUpdate({ [name]: value });
      return updatedInfo;
    });
    setInformationProvider(value);
  };

  const validateField = (name, value) => {
    let errors = { ...validationErrors };

    switch (name) {
      case 'updatedInformationProvider':
        if (generalInfo?.isInformationProviderChanged && !value) {
          errors[name] = 'Please select an updated information provider';
        } else {
          delete errors[name];
        }
        break;

      

      

      default:
        break;
    }

    setValidationErrors(errors);
  };

  const customStyles = (hasError) => ({
    control: (provided) => ({
      ...provided,
      borderColor: hasError ? '#dc3545' : provided.borderColor,
      '&:hover': {
        borderColor: hasError ? '#dc3545' : provided.borderColor,
      },
      boxShadow: hasError ? '0 0 10px rgba(220, 53, 69, 0.5)' : provided.boxShadow,
    }),
  });

  const isDisabled = isDisableForUsers;
  const provider = generalInfo?.informationProvider;
  const isProviderMissing = !provider || provider.trim() === "";

  return (
    <Col lg={12} className="mb-3">
      <Card>
        <Card.Body>
          <Card.Title>General Information</Card.Title>
          <Row className="mb-3">
            <Col lg={12} className="mb-3">
              <div className="d-flex align-items-center">
                <Form.Label>
                  Our records indicate Clarity currently receives ongoing contribution data via{" "}
                  <strong>
                    {isProviderMissing
                      ? onlyHra
                        ? "Does not apply for Prefunded HRA Plans"
                        : "Not Indicated"
                      : provider}
                  </strong>
                  . Is this changing?
                </Form.Label>
                <div className="form-check form-switch form-toggle">
                  <input
                    type="checkbox"
                    className="form-check-input-toggel form-check-input toggle-input-switch is-change-toggle"
                    role="switch"
                    aria-label="Toggle information provider change"
                    checked={generalInfo?.informationProvider === INFORMATION_PROVIDERS.NOT_INDICATED || generalInfo?.isInformationProviderChanged}
                    onChange={(e) => handleToggleChange(e, 'isInformationProviderChanged')}
                    disabled={isDisabled || generalInfo?.informationProvider === INFORMATION_PROVIDERS.NOT_INDICATED}
                  />
                </div>
              </div>
            </Col>
          </Row>

          {generalInfo?.isInformationProviderChanged && (
            <>
            <Row className="mb-3">
              <Col lg={3} className="mb-3">
                <Form.Label>Contributions will be credited to participants accounts via</Form.Label>
              </Col>
              <Col lg={3} className="mb-3">
                <Select
                  name="updatedInformationProvider"
                  placeholder="Please select"
                  options={InformationProviderOptions.filter((option) => option.value !== generalInfo?.informationProvider)}
                  value={InformationProviderOptions.find((option) => option.value === generalInfo?.updatedInformationProvider)}
                  onChange={handleSelectChange}
                  styles={customStyles(validationErrors && validationErrors.updatedInformationProvider)}
                  isDisabled={isDisabled}
                />
                {validationErrors && validationErrors.updatedInformationProvider && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.updatedInformationProvider}
                  </div>
                )}
              </Col>
            </Row>
            {generalInfo?.updatedInformationProvider?.includes('Clarity Comprehensive API') && (
              <Row className="mb-5">
                  
                  <em>API Terms &amp; Conditions - Electronic Acceptance: By selecting “Clarity Comprehensive API” above and 
                                    completing this renewal, you confirm that you (the “Client") have read and accept the 
                                    <a href='https://kits.claritybenefitsolutions.com/api-integration-terms-and-conditions/' target='_blank'>
                                    API Integration Terms and Conditions
                                    </a> 
                                    and are duly authorized to bind the Client identified above. Your electronic acceptance and related electronic 
                                    records have the same legal effect as a signed writing under applicable law. The Client’s continued use of the 
                                    API Integration services also constitute acceptance of those terms.</em>
                                    </Row>
          )}
            </>
            

            
          )}

          {generalInfo?.isInformationProviderChanged && generalInfo?.updatedInformationProvider !== "" && (
            <Row className="mb-3">
              <Col lg={12}>
                <PayScheduleFrequency
                  ref={payscheduleRef}
                  caseId={caseId}
                  renewalCase={renewalCase}
                  isDisableForUsers={isDisableForUsers}
                  initialFormValues={{}}
                />
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
});

export default versioned(GeneralInfo, LegacyExperience);
